import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  Loader2,
  Shield,
  LogOut,
  ArrowLeft,
  MessageSquare,
  AlertTriangle,
  Radio,
  Flag,
  Lock,
  Eye,
  EyeOff,
  Trash2,
  Ban,
  CheckCircle,
  Users,
} from 'lucide-react';
import { Logo } from '../components/Logo';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminCommunityPage() {
  const { token, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    loungePosts: [],
    loungeComments: [],
    liveChatMessages: [],
    providerAlerts: [],
  });
  const [muted, setMuted] = useState([]);
  const [activeTab, setActiveTab] = useState('posts');
  const [muteDialog, setMuteDialog] = useState(false);
  const [muteForm, setMuteForm] = useState({
    providerId: '',
    feature: 'LIVE_CHAT',
    hours: 24,
    reason: '',
  });

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [reportedRes, mutedRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/community/reported`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API_URL}/api/admin/community/muted`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      setData(reportedRes.data || data);
      setMuted(mutedRes.data || []);
    } catch (e) {
      toast.error('Failed to load moderation data');
    } finally {
      setLoading(false);
    }
  };

  const moderate = async (kind, id, action) => {
    if (action === 'delete' && !window.confirm('Delete permanently?')) return;
    try {
      await axios.patch(
        `${API_URL}/api/admin/community/${kind}/${id}?action=${action}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Action applied');
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    }
  };

  const deleteAlert = async (id) => {
    if (!window.confirm('Delete this alert permanently?')) return;
    try {
      await axios.patch(
        `${API_URL}/api/admin/community/alerts/${id}?action=delete`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Alert deleted');
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    }
  };

  const submitMute = async () => {
    if (!muteForm.providerId) {
      toast.error('Provider ID required');
      return;
    }
    try {
      const params = new URLSearchParams({
        providerId: muteForm.providerId,
        feature: muteForm.feature,
        hours: String(muteForm.hours),
        reason: muteForm.reason || 'Admin action',
      });
      await axios.post(
        `${API_URL}/api/admin/community/mute?${params.toString()}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Provider muted');
      setMuteDialog(false);
      setMuteForm({ providerId: '', feature: 'LIVE_CHAT', hours: 24, reason: '' });
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to mute');
    }
  };

  const unmute = async (muteId) => {
    if (!window.confirm('Unmute this provider?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/community/mute/${muteId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Provider unmuted');
      loadAll();
    } catch (err) {
      toast.error('Failed to unmute');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return '';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const counts = {
    posts: data.loungePosts?.length || 0,
    comments: data.loungeComments?.length || 0,
    chat: data.liveChatMessages?.length || 0,
    alerts: data.providerAlerts?.length || 0,
    muted: muted.length,
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-card border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center gap-4">
              <Logo variant="compact" size="sm" />
              <div className="hidden sm:flex items-center gap-2 border-l pl-4">
                <Shield className="h-5 w-5 text-primary" />
                <h1 className="text-lg font-semibold">Community Moderation</h1>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={() => navigate('/admin')}>
                <ArrowLeft className="h-4 w-4 mr-1" /> Admin
              </Button>
              <Button variant="outline" size="sm" onClick={handleLogout}>
                <LogOut className="h-4 w-4 mr-1" /> Logout
              </Button>
            </div>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          <Card>
            <CardContent className="pt-4">
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <MessageSquare className="h-3 w-3" /> Reported Posts
              </p>
              <p className="text-2xl font-bold">{counts.posts}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <MessageSquare className="h-3 w-3" /> Reported Comments
              </p>
              <p className="text-2xl font-bold">{counts.comments}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <Radio className="h-3 w-3" /> Reported Chat
              </p>
              <p className="text-2xl font-bold">{counts.chat}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" /> Disputed Alerts
              </p>
              <p className="text-2xl font-bold">{counts.alerts}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <Ban className="h-3 w-3" /> Muted Providers
              </p>
              <p className="text-2xl font-bold">{counts.muted}</p>
            </CardContent>
          </Card>
        </div>

        <div className="flex justify-end mb-4">
          <Button onClick={() => setMuteDialog(true)} variant="outline" data-testid="open-mute-dialog">
            <Ban className="h-4 w-4 mr-2" /> Mute a Provider
          </Button>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-5 max-w-3xl">
            <TabsTrigger value="posts" data-testid="tab-posts">Posts ({counts.posts})</TabsTrigger>
            <TabsTrigger value="comments" data-testid="tab-comments">Comments ({counts.comments})</TabsTrigger>
            <TabsTrigger value="chat" data-testid="tab-chat">Chat ({counts.chat})</TabsTrigger>
            <TabsTrigger value="alerts" data-testid="tab-alerts">Alerts ({counts.alerts})</TabsTrigger>
            <TabsTrigger value="muted" data-testid="tab-muted">Muted ({counts.muted})</TabsTrigger>
          </TabsList>

          {/* Posts */}
          <TabsContent value="posts" className="space-y-3 mt-4">
            {data.loungePosts.length === 0 ? (
              <EmptyState text="No reported posts" />
            ) : (
              data.loungePosts.map((p) => (
                <Card key={p.id} data-testid={`mod-post-${p.id}`}>
                  <CardHeader>
                    <div className="flex justify-between items-start gap-2 flex-wrap">
                      <div>
                        <CardTitle className="text-base">{p.title}</CardTitle>
                        <p className="text-xs text-muted-foreground mt-1">
                          {p.providerName} • {p.category} • {formatDate(p.createdAt)}
                        </p>
                      </div>
                      <Badge variant="outline" className="text-red-700 border-red-300">
                        <Flag className="h-3 w-3 mr-1" /> {p.reportCount} reports
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm whitespace-pre-wrap line-clamp-4">{p.body}</p>
                    <div className="flex flex-wrap gap-2 mt-3">
                      <Badge variant="secondary">Status: {p.status}</Badge>
                      {p.status !== 'HIDDEN' ? (
                        <Button size="sm" variant="outline" onClick={() => moderate('posts', p.id, 'hide')}>
                          <EyeOff className="h-3 w-3 mr-1" /> Hide
                        </Button>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => moderate('posts', p.id, 'restore')}>
                          <Eye className="h-3 w-3 mr-1" /> Restore
                        </Button>
                      )}
                      {p.status !== 'LOCKED' ? (
                        <Button size="sm" variant="outline" onClick={() => moderate('posts', p.id, 'lock')}>
                          <Lock className="h-3 w-3 mr-1" /> Lock
                        </Button>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => moderate('posts', p.id, 'unlock')}>
                          <Lock className="h-3 w-3 mr-1" /> Unlock
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600"
                        onClick={() => moderate('posts', p.id, 'delete')}
                      >
                        <Trash2 className="h-3 w-3 mr-1" /> Delete
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setMuteForm({ ...muteForm, providerId: p.providerId, feature: 'LOUNGE' });
                          setMuteDialog(true);
                        }}
                      >
                        <Ban className="h-3 w-3 mr-1" /> Mute author
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>

          {/* Comments */}
          <TabsContent value="comments" className="space-y-3 mt-4">
            {data.loungeComments.length === 0 ? (
              <EmptyState text="No reported comments" />
            ) : (
              data.loungeComments.map((c) => (
                <Card key={c.id}>
                  <CardContent className="pt-4">
                    <div className="flex justify-between items-start gap-2 flex-wrap">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-muted-foreground">
                          {c.providerName} • {formatDate(c.createdAt)}
                        </p>
                        <p className="text-sm whitespace-pre-wrap mt-1">{c.body}</p>
                      </div>
                      <Badge variant="outline" className="text-red-700 border-red-300">
                        <Flag className="h-3 w-3 mr-1" /> {c.reportCount}
                      </Badge>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-3">
                      <Badge variant="secondary">Status: {c.status}</Badge>
                      {c.status !== 'HIDDEN' ? (
                        <Button size="sm" variant="outline" onClick={() => moderate('comments', c.id, 'hide')}>
                          <EyeOff className="h-3 w-3 mr-1" /> Hide
                        </Button>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => moderate('comments', c.id, 'restore')}>
                          <Eye className="h-3 w-3 mr-1" /> Restore
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600"
                        onClick={() => moderate('comments', c.id, 'delete')}
                      >
                        <Trash2 className="h-3 w-3 mr-1" /> Delete
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>

          {/* Chat */}
          <TabsContent value="chat" className="space-y-3 mt-4">
            {data.liveChatMessages.length === 0 ? (
              <EmptyState text="No reported chat messages" />
            ) : (
              data.liveChatMessages.map((m) => (
                <Card key={m.id}>
                  <CardContent className="pt-4">
                    <div className="flex justify-between items-start gap-2 flex-wrap">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-muted-foreground">
                          {m.providerName} • #{m.room} • {formatDate(m.createdAt)}
                        </p>
                        <p className="text-sm whitespace-pre-wrap mt-1">{m.body}</p>
                      </div>
                      <Badge variant="outline" className="text-red-700 border-red-300">
                        <Flag className="h-3 w-3 mr-1" /> {m.reportCount}
                      </Badge>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-3">
                      <Badge variant="secondary">Status: {m.status}</Badge>
                      {m.status !== 'HIDDEN' ? (
                        <Button size="sm" variant="outline" onClick={() => moderate('chat', m.id, 'hide')}>
                          <EyeOff className="h-3 w-3 mr-1" /> Hide
                        </Button>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => moderate('chat', m.id, 'restore')}>
                          <Eye className="h-3 w-3 mr-1" /> Restore
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600"
                        onClick={() => moderate('chat', m.id, 'delete')}
                      >
                        <Trash2 className="h-3 w-3 mr-1" /> Delete
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setMuteForm({ ...muteForm, providerId: m.providerId, feature: 'LIVE_CHAT' });
                          setMuteDialog(true);
                        }}
                      >
                        <Ban className="h-3 w-3 mr-1" /> Mute author
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>

          {/* Alerts */}
          <TabsContent value="alerts" className="space-y-3 mt-4">
            {data.providerAlerts.length === 0 ? (
              <EmptyState text="No disputed alerts" />
            ) : (
              data.providerAlerts.map((a) => (
                <Card key={a.id}>
                  <CardHeader>
                    <CardTitle className="text-base">{a.targetClientName}</CardTitle>
                    <p className="text-xs text-muted-foreground">
                      {a.reportingProviderName} • {formatDate(a.createdAt)} • {a.reportCount} reports
                    </p>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm whitespace-pre-wrap line-clamp-4">{a.description}</p>
                    <div className="flex flex-wrap gap-2 mt-3">
                      <Badge variant="secondary">Status: {a.status}</Badge>
                      <Button size="sm" variant="outline" onClick={() => moderate('alerts', a.id, 'activate')}>
                        <CheckCircle className="h-3 w-3 mr-1" /> Restore Active
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => moderate('alerts', a.id, 'resolve')}>
                        <CheckCircle className="h-3 w-3 mr-1" /> Resolve
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => moderate('alerts', a.id, 'hide')}>
                        <EyeOff className="h-3 w-3 mr-1" /> Hide
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600"
                        onClick={() => deleteAlert(a.id)}
                      >
                        <Trash2 className="h-3 w-3 mr-1" /> Delete
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>

          {/* Muted */}
          <TabsContent value="muted" className="space-y-3 mt-4">
            {muted.length === 0 ? (
              <EmptyState text="No muted providers" />
            ) : (
              muted.map((m) => (
                <Card key={m.id}>
                  <CardContent className="pt-4">
                    <div className="flex justify-between items-start gap-3 flex-wrap">
                      <div>
                        <p className="font-medium flex items-center gap-2">
                          <Users className="h-4 w-4" /> {m.providerName || m.providerId}
                          {m.businessName && (
                            <span className="text-muted-foreground font-normal">
                              • {m.businessName}
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Feature: <Badge variant="secondary">{m.feature}</Badge> • Reason: {m.reason}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Expires: {formatDate(m.expiresAt)}
                        </p>
                      </div>
                      <Button size="sm" variant="outline" onClick={() => unmute(m.id)}>
                        <CheckCircle className="h-3 w-3 mr-1" /> Unmute
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Mute Dialog */}
      <Dialog open={muteDialog} onOpenChange={setMuteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mute a Provider</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Provider ID</Label>
              <Input
                value={muteForm.providerId}
                onChange={(e) => setMuteForm({ ...muteForm, providerId: e.target.value })}
                placeholder="Provider UUID"
                data-testid="mute-provider-id"
              />
            </div>
            <div>
              <Label>Feature</Label>
              <Select
                value={muteForm.feature}
                onValueChange={(v) => setMuteForm({ ...muteForm, feature: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="LIVE_CHAT">Live Chat</SelectItem>
                  <SelectItem value="LOUNGE">Lounge</SelectItem>
                  <SelectItem value="ALL">All Community</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Duration (hours)</Label>
              <Input
                type="number"
                min={1}
                value={muteForm.hours}
                onChange={(e) => setMuteForm({ ...muteForm, hours: parseInt(e.target.value) || 24 })}
              />
            </div>
            <div>
              <Label>Reason</Label>
              <Input
                value={muteForm.reason}
                onChange={(e) => setMuteForm({ ...muteForm, reason: e.target.value })}
                placeholder="Reason for muting"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMuteDialog(false)}>
              Cancel
            </Button>
            <Button onClick={submitMute} data-testid="submit-mute">
              <Ban className="h-4 w-4 mr-2" /> Mute Provider
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function EmptyState({ text }) {
  return (
    <Card>
      <CardContent className="p-8 text-center text-muted-foreground">
        <CheckCircle className="h-10 w-10 mx-auto mb-2 text-green-500 opacity-60" />
        <p>{text}</p>
      </CardContent>
    </Card>
  );
}
